import { api } from './http';
import type { DirectoryUser, UserFull, UserSummary } from './types';

export const getUsers = (params?: { active_only?: boolean }) => {
  // Guard: React Query passes its context object as first arg when used as queryFn directly.
  const safeParams = params && typeof params === 'object' && 'active_only' in params ? params : undefined;
  return api.get<UserSummary[]>('/users', { params: safeParams }).then(r => r.data);
};

export const getUsersWithoutTimeclock = () =>
  api.get<UserSummary[]>('/users/unlinked-timeclock').then(r => r.data);

export const getCurrentUser = () =>
  api.get<UserFull>('/users/me').then(r => r.data);

export const updateMySettings = (data: { display_title?: string | null; todoist_id?: string | null; ringcentral_extension?: string | null; m365_mailbox?: string | null; email_signature?: string | null; email_signature_enabled?: boolean; todoist_mirror_opt_out?: boolean; buzz_urgent_tasks?: boolean }) =>
  api.patch<UserFull>('/users/me', data).then(r => r.data);

export type MyMaverickSettings = {
  enabled: boolean;
  api_key_configured: boolean;
  api_key_masked: string | null;
  api_key_configured_at: string | null;
  updated_at: string | null;
  search_url_configured: boolean;
  statute_search_url_configured: boolean;
  legacy_backend_enabled: boolean;
  legacy_env_key_configured: boolean;
  backend_available: boolean;
};

export type MyMaverickTestResult = {
  ok: boolean;
  backend: string;
  result_count: number;
  api_key_masked: string | null;
};

export const getMyMaverickSettings = () =>
  api.get<MyMaverickSettings>('/users/me/maverick').then(r => r.data);

export const updateMyMaverickSettings = (data: { enabled?: boolean; api_key?: string; clear_api_key?: boolean }) =>
  api.patch<MyMaverickSettings>('/users/me/maverick', data).then(r => r.data);

export const testMyMaverickConnection = () =>
  api.post<MyMaverickTestResult>('/users/me/maverick/test').then(r => r.data);

/** P3-A5: persists the install-coach "Not now" / "Done — it's installed"
 * dismissal server-side. localStorage is only a same-device fast-path cache
 * (InstallCoach.tsx) — Safari's ITP purges it after ~7 days, so this is the
 * durable source of truth that stops the coach from re-nagging. */
export const dismissA2hsCoach = () =>
  api.post('/users/me/a2hs-coach-dismissed').then(r => r.data);

export const getUserDetail = (id: number) =>
  api.get<UserFull>(`/users/${id}`).then(r => r.data);

export const updateUser = (id: number, data: Partial<UserFull>) =>
  api.patch<UserFull>(`/users/${id}`, data).then(r => r.data);

export interface SabrinaSyncResult {
  ok: boolean;
  sabrina_employee_id?: number;
  status?: 'created' | 'already_exists';
  error?: string;
}

export interface UserFullWithSync extends UserFull {
  sabrina_sync?: SabrinaSyncResult;
}

export const importUserFromAD = (data: {
  azure_ad_id: string;
  email: string;
  name: string;
  role?: string;
  team?: string | null;
  job_title?: string | null;
  department?: string | null;
}) =>
  api.post<UserFullWithSync>('/users/import', data).then(r => r.data);

export const deactivateUser = (id: number) =>
  api.delete(`/users/${id}`).then(r => r.data);

export const getPermissions = () =>
  api.get<Record<string, string[]>>('/users/permissions').then(r => r.data);

export const getDirectoryUsers = (search?: string) =>
  api.get<DirectoryUser[]>('/directory/users', { params: search ? { search } : {} }).then(r => r.data);

// ── Native auth user management ─────────────────────────

export const createNativeUser = (data: {
  email: string;
  name: string;
  role: string;
  team?: string | null;
  contact_email?: string | null;
  temp_password: string;
}) =>
  api.post<UserFull>('/users/native', data).then(r => r.data);

export const resetNativePassword = (id: number, temp_password: string) =>
  api.post(`/users/${id}/reset-password`, { temp_password }).then(r => r.data);

export const clearNative2FA = (id: number) =>
  api.post(`/users/${id}/clear-2fa`).then(r => r.data);

export const convertToMicrosoft = (id: number, m365_email: string) =>
  api.post(`/users/${id}/convert-to-microsoft`, { m365_email }).then(r => r.data);
